"use client";

import { useCallback, useState } from "react";
import { CameraFramingControls, NO_PREVIEW, type FramingPreview } from "@/components/live/camera-framing-controls";
import { VideoWidget } from "@/components/roll-widgets/video-widget";
import { useLiveSession } from "@/contexts/live-session-context";
import { useHandheld } from "@/hooks/use-short-viewport";
import { recitalSourcesToShow } from "@/lib/live/recital/recital-video";
import { observeDisplayedFrames } from "@/lib/live/media-sync/media-sync";
import { DEFAULT_FRAMING } from "@/lib/live/video/camera-framing";
import type { LocalCamera, VideoSource } from "@/lib/live/video/video-types";

/**
 * Les vidéos du récital, en vignettes sur le roll : une par caméra, deux au plus.
 *
 * # Chez le spectateur
 *
 * Les caméras reçues, au nom de l'interprète, et rien tant qu'il ne filme pas :
 * une vignette vide sur chaque rouleau annoncerait une image qui ne viendra
 * peut-être jamais.
 *
 * # Chez l'interprète
 *
 * Son propre retour, pour cadrer : la webcam en miroir, comme dans une glace —
 * sans quoi la main droite levée se voit lever la gauche —, et chaque téléphone
 * tel quel, comme le public le reçoit.
 *
 * Les sources viennent du moteur vidéo de la séance ; lesquelles montrer se
 * décide dans `recital-video.ts`.
 *
 * En bas à gauche par défaut, empilées : le panneau du récital s'ouvre à
 * droite, par-dessus le roll. En bulles sur un téléphone couché, où une
 * vignette de 320 px couvrirait le rouleau qu'on est venu regarder.
 *
 * # Cadrer
 *
 * L'interprète cadre depuis la vignette mise en avant : c'est le seul endroit
 * où il voit l'image assez grande pour savoir ce qu'il coupe. Le spectateur
 * peut aussi l'agrandir — il n'a rien à commander, et les commandes ne lui
 * sont pas rendues.
 *
 * Ce qu'il règle ne s'applique pas à son écran mais à l'appareil qui filme,
 * avant son encodeur. Sa webcam est ici, entière : il cadre dessus, en local,
 * et valide. D'un téléphone il ne reçoit que le cadré, et cadre sur ce qui lui
 * revient. Voir `camera-framing-controls.tsx` et `camera-framing.ts`.
 *
 * # Derrière le rideau
 *
 * Rien pour la salle : les caméras sont sorties de la salle, il n'y a plus de
 * source à montrer. Chez l'interprète, sa webcam reste — la piste est sortie
 * sans s'éteindre, et c'est précisément pour cadrer qu'on baisse le rideau. La
 * vignette le dit, pour qu'on ne la prenne pas pour ce que la salle voit. Un
 * téléphone, lui, se cadre sur son propre écran : ses images ne traversent plus
 * la salle, donc personne ici ne peut les afficher.
 */
export function RecitalCamera() {
  const live = useLiveSession();
  const recital = live?.session?.recital ?? null;
  // Suivi à la rotation, et non retenu au montage : on entre toujours par le
  // portrait — le studio y demande de tourner l'appareil avant de se montrer.
  // Voir `collapsedByDefault`.
  //
  // Le coin, lui, reste en bas à gauche dans les deux orientations : un récital
  // en direct ne se regarde pas encore debout (`handheld-notice.tsx` y demande
  // toujours de tourner l'appareil), donc le cas du portrait ne se présente
  // pas, et l'inventer serait inventer aussi ce qui s'y passe.
  const collapsed = useHandheld();

  if (live === null || recital === null) {
    return null;
  }

  const name = live.session?.owner.nickname ?? "Interprète";
  const webcam = live.curtain && !live.isListener ? live.localCamera : null;
  const mine = live.identity ?? null;

  if (webcam !== null) {
    return <CurtainWebcam name={name} webcam={webcam} identity={mine} collapsed={collapsed} />;
  }

  const shown = recitalSourcesToShow(live.videoSources, { listener: live.isListener, self: live.videoSelf });

  return (
    <>
      {shown.map((source, index) => (
        <SourceWidget
          // Une vignette par caméra, gardée tant qu'elle filme : quand l'autre
          // s'en va, celle qui reste ne se démonte pas et garde son coin. La webcam
          // de ce poste garde la sienne quand LiveKit la republie sous un autre sid.
          key={source.local ? "webcam" : source.id}
          source={source}
          name={name}
          label={shown.length > 1 ? `caméra ${index + 1}` : undefined}
          collapsed={collapsed}
        />
      ))}
    </>
  );
}

/**
 * Les commandes de cadrage de la webcam de ce poste, rangée sous son identité
 * LiveKit dans la table de la séance.
 *
 * Un composant à part parce qu'il lit la séance lui-même : l'écrire en ligne
 * dans la vignette du rideau aurait demandé d'y traîner l'identité et la table
 * jusque dans une branche qui n'en parle pas.
 */
function WebcamFramingControls({
  identity,
  frame,
  source,
  onPreview,
}: {
  readonly identity: string;
  readonly frame: { readonly width: number; readonly height: number };
  /** La webcam telle qu'elle filme, avant cadrage. */
  readonly source: MediaStream;
  readonly onPreview: (preview: FramingPreview) => void;
}) {
  const live = useLiveSession();

  if (live === null) {
    return null;
  }

  return (
    <CameraFramingControls
      framing={live.cameras[identity] ?? DEFAULT_FRAMING}
      participant={identity}
      readDeclared={live.fetchDeclaredCameras}
      frame={frame}
      source={source}
      mirrored
      onPreview={onPreview}
      onChange={(framing) => live.setCameraFraming(identity, framing)}
    />
  );
}

/**
 * Une caméra du récital, sur le roll.
 *
 * Un composant à part pour une seule raison : la qualité qu'on demande à la
 * salle dépend de la taille à laquelle on montre l'image, donc la vignette
 * doit pouvoir dire quand elle passe au premier plan — et le rappel qui le dit
 * doit être stable, sous peine de rebrancher son effet à chaque liste reçue.
 */
function SourceWidget({
  source,
  name,
  label,
  collapsed,
}: {
  readonly source: VideoSource;
  readonly name: string;
  readonly label?: string;
  readonly collapsed: boolean;
}) {
  const live = useLiveSession();
  const [preview, setPreview] = useState<FramingPreview>(NO_PREVIEW);
  const id = source.id;
  const watch = live?.watchVideo;

  // En grand, la couche haute : la même image étalée sur tout le rouleau
  // demande la définition de la capture, sans quoi on agrandit du 360p — c'était
  // le flou du 2026-09-20. Dans un coin, la vignette reprend sa 360p.
  const onAhead = useCallback((ahead: boolean) => watch?.(id, ahead ? "high" : null), [watch, id]);

  if (live === null) {
    return null;
  }

  // La webcam de ce poste se cadre sur son image entière, qui est ici ; un
  // téléphone, sur ce qui revient de lui.
  const raw = source.local ? (live.localCamera?.source ?? null) : null;

  return (
    <VideoWidget
      name={name}
      label={label}
      stream={source.stream}
      mirrored={source.local}
      anchorByDefault="bottom-left"
      collapsedByDefault={collapsed}
      // Une image reçue : la page mesure quand elle s'affiche vraiment, pour
      // que la retenue vise l'écran et non le décodeur. La webcam de ce poste
      // ne traverse rien.
      observeFrames={source.local ? undefined : observeDisplayedFrames}
      onAhead={onAhead}
      previewTransform={preview.transform}
      previewAspect={preview.aspect}
      controls={
        live.isListener
          ? undefined
          : (frame) => (
              <CameraFramingControls
                framing={live.cameras[source.participant] ?? DEFAULT_FRAMING}
                participant={source.participant}
                readDeclared={live.fetchDeclaredCameras}
                frame={frame}
                source={raw}
                mirrored={source.local}
                onPreview={setPreview}
                onChange={(framing) => live.setCameraFraming(source.participant, framing)}
              />
            )
      }
    />
  );
}

/**
 * La webcam de ce poste derrière le rideau : son retour, pour cadrer.
 *
 * Cadrer derrière le rideau est le cas qu'on vient chercher — le geste ne
 * s'applique qu'à la capture, et rien de ce qu'on essaie ne sort de la scène.
 *
 * L'identité LiveKit de ce poste, et non `videoSelf` : celui-ci nomme un
 * **compte**, qui peut avoir deux onglets et deux caméras, quand la table de
 * cadrage range une caméra par publieur.
 */
function CurtainWebcam({
  name,
  webcam,
  identity,
  collapsed,
}: {
  readonly name: string;
  readonly webcam: LocalCamera;
  readonly identity: string | null;
  readonly collapsed: boolean;
}) {
  const [preview, setPreview] = useState<FramingPreview>(NO_PREVIEW);

  return (
    <VideoWidget
      key="webcam"
      name={name}
      label="hors antenne"
      stream={webcam.stream}
      mirrored
      // La pastille « Direct » serait ici le contraire de la vérité : cette
      // image ne sort pas de la pièce.
      liveBadge={false}
      anchorByDefault="bottom-left"
      collapsedByDefault={collapsed}
      previewTransform={preview.transform}
      previewAspect={preview.aspect}
      controls={
        identity === null
          ? undefined
          : (frame) => (
              <WebcamFramingControls identity={identity} frame={frame} source={webcam.source} onPreview={setPreview} />
            )
      }
    />
  );
}
